import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 1000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        // GK-HUB: Separate action for tab-visible restore (updates Redis + DB availability)
        restorePresence() {
          this.perform('restore_presence');
        },
        // GK-HUB: end restorePresence
        received: this.onReceived,
        disconnected: () => {
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL);
    };
    this.triggerPresenceInterval();

    // GK-HUB: Force immediate presence restore when tab becomes visible again
    // Browsers throttle setTimeout in background tabs (Chrome: 1min+), so the
    // heartbeat stops and Redis presence expires (20s TTL) → agent shows offline.
    // Uses restorePresence (not updatePresence) so the server also restores DB availability.
    // Regular heartbeats from background tabs only update Redis, not DB.
    this.handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (this.consumer.connection.isOpen()) {
          this.subscription.restorePresence();
        }
      }
    };
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    // GK-HUB: end visibilitychange presence restore
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      // GK-HUB: Force presence restore on reconnect — restores Redis + DB availability
      // immediately instead of waiting for next heartbeat cycle (up to 20s)
      this.subscription.restorePresence();
      // GK-HUB: end reconnect presence
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    // GK-HUB: Remove visibilitychange listener on disconnect (cleanup)
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
